import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router';
import {
  Badge,
  IconButton,
  List,
  Typography,
  CircularProgress,
  Box,
  Divider,
  Button,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton as MuiIconButton,
  Tooltip,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { useWatchList } from 'src/contexts/WatchListContext';
import useAuth from 'src/hooks/useAuth';
import MenuPopover from 'src/components/MenuPopover';
import { Z_INDEX } from 'src/constants/zIndex';
import { useEventTracking } from 'src/hooks/useEventTracking';
import {
  trackWatchListRemove,
  trackWatchListRemoveAll,
} from 'src/utils/analyticsTracking';
import { WATCH_LIST_SOURCES } from 'src/constants/analytics';

import { WatchListEyeProps } from 'src/types/WatchList';

export default function WatchListEye({
  iconSize = 24,
  isOpen,
  onToggle,
  onClose,
}: WatchListEyeProps): React.JSX.Element {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const iconButtonRef = useRef<HTMLButtonElement>(null);
  const { trackDropdownAction } = useEventTracking();

  // Always call the hook first (React hooks rule)
  const {
    watchedCerts,
    watchedCertsCount,
    loading,
    error,
    handleRemoveWatch,
    handleBatchRemove,
  } = useWatchList();

  const hasWatchPermission = user?.canUseRealTimeNotificationsWatched;

  // Early return if no permission - this is the most likely issue
  if (!hasWatchPermission) {
    return null;
  }

  // Show error state if there's a context error
  if (error) {
    return (
      <Tooltip title="Watch List (Error loading data)" placement="bottom">
        <IconButton
          color="inherit"
          size="large"
          className="watchlist-eye__icon-button"
          style={{
            width: iconSize,
            height: iconSize,
            minWidth: iconSize,
            minHeight: iconSize,
          }}
        >
          <VisibilityIcon color="error" sx={{ fontSize: iconSize }} />
        </IconButton>
      </Tooltip>
    );
  }

  const handleEyeClick = (event: React.MouseEvent<HTMLElement>): void => {
    if (!hasWatchPermission) {
      return;
    }

    // Use external state management if provided, otherwise use internal state
    if (onToggle) {
      onToggle();
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = (): void => {
    if (onClose) {
      onClose();
    } else {
      setAnchorEl(null);
    }
  };

  const handleUnwatch = async (certId: number): Promise<void> => {
    // Track individual unwatch action
    const cert = watchedCerts.find((c) => c.id === certId);

    // Track with new analytics function for header context
    trackWatchListRemove(WATCH_LIST_SOURCES.HEADER, [certId], {
      property_id: cert?.property?.id,
      household_id: cert?.household?.id,
      household_name: cert?.household?.name,
    });

    // Keep existing tracking for backward compatibility
    trackDropdownAction('watchlist', 'remove_from_watchlist', {
      certification_id: certId,
      property_id: cert?.property?.id,
      household_id: cert?.household?.id,
      household_name: cert?.household?.name,
    });

    await handleRemoveWatch([certId]);
  };

  const handleUnwatchAll = async (): Promise<void> => {
    if (watchedCerts.length === 0) {
      return;
    }

    // Track with new analytics function for header context
    trackWatchListRemoveAll(WATCH_LIST_SOURCES.HEADER, watchedCerts.length);

    // Keep existing tracking for backward compatibility
    trackDropdownAction('watchlist', 'unwatch_all', {
      certification_count: watchedCerts.length,
    });

    const certIds = watchedCerts.map((cert) => cert.id);
    await handleBatchRemove(certIds);
  };

  const handleCertificationClick = (cert: {
    id: number;
    property?: { slug: string };
    household?: { id: number; name: string };
  }): void => {
    // Track certification click from watch list
    trackDropdownAction('watchlist', 'watchlist_certification_click', {
      certification_id: cert.id,
      property_id: cert.property?.id,
      household_id: cert.household?.id,
      household_name: cert.household?.name,
      property_slug: cert.property?.slug,
      destination: cert.property?.slug
        ? `/workbench/${cert.property.slug}/${cert.id}/`
        : `/certification/${cert.id}`,
    });

    if (cert.property?.slug) {
      navigate(`/workbench/${cert.property.slug}/${cert.id}/`);
    } else {
      // Fallback if no property slug available
      navigate(`/certification/${cert.id}`);
    }
    handleClose();
  };

  // Determine if popover should be open
  const isPopoverOpen = onToggle ? isOpen : Boolean(anchorEl);

  if (!hasWatchPermission) {
    return null;
  }

  return (
    <>
      <span>
        <IconButton
          ref={iconButtonRef}
          color="inherit"
          onClick={handleEyeClick}
          size="large"
          className="watchlist-eye__icon-button"
          style={{
            width: iconSize,
            height: iconSize,
            minWidth: iconSize,
            minHeight: iconSize,
          }}
        >
          <Badge
            badgeContent={watchedCertsCount > 0 ? watchedCertsCount : null}
            color="primary"
            overlap="circular"
            className="watchlist-eye__badge"
          >
            <VisibilityIcon style={{ fontSize: iconSize, color: '#1976d2' }} />
          </Badge>
        </IconButton>
      </span>
      <MenuPopover
        open={isPopoverOpen}
        onClose={handleClose}
        anchorEl={iconButtonRef.current}
        sx={{ width: 660 }}
        container={document.body}
        slotProps={{
          paper: {
            sx: {
              zIndex: Z_INDEX.HEADER_DROPDOWN,
            },
          },
        }}
      >
        <Box className="watchlist-eye__popover-header">
          <Box>
            <Typography variant="subtitle1">Watch List</Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              className="watchlist-eye__popover-header-title"
            >
              Click anywhere in a row to open the certification
            </Typography>
          </Box>
          <Button
            size="small"
            onClick={handleUnwatchAll}
            disabled={watchedCerts.length === 0}
            color="error"
            className="watchlist-eye__popover-unwatch-btn"
          >
            Unwatch All
          </Button>
        </Box>
        <Divider />
        {loading ? (
          <Box
            className="watchlist-eye__loading"
            style={{ display: 'flex', justifyContent: 'center' }}
          >
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Typography color="error" className="watchlist-eye__error">
            {error}
          </Typography>
        ) : watchedCerts.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <VisibilityIcon
              sx={{
                fontSize: 48,
                color: 'text.secondary',
                mb: 1,
                opacity: 0.6,
              }}
            />
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
                mb: 0.5,
              }}
            >
              Your watch list is empty
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                opacity: 0.8,
              }}
            >
              Start watching certifications to get notified about important
              updates.
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding className="watchlist-eye__list">
            {watchedCerts.map((cert) => (
              <ListItem
                key={cert.id}
                component="button"
                onClick={() => handleCertificationClick(cert)}
                className="watchlist-eye__list-item"
              >
                <ListItemText
                  primary={
                    <Typography variant="body2" component="div" noWrap>
                      {cert.household?.name || 'No household'}
                    </Typography>
                  }
                  secondary={
                    <Box>
                      <Typography
                        variant="caption"
                        component="div"
                        color="text.secondary"
                        noWrap
                      >
                        {cert.property?.name || 'No property'} |{' '}
                        {cert.category?.name || 'No category'}
                      </Typography>
                      <Typography
                        variant="caption"
                        component="div"
                        color="text.secondary"
                        display="block"
                        noWrap
                      >
                        {cert.certification_config?.name ||
                          'No certification config'}
                      </Typography>
                      <Box
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                          marginTop: 4,
                        }}
                      >
                        <AdminPanelSettingsIcon className="watchlist-eye__list-item-icon" />
                        <Typography
                          variant="caption"
                          component="div"
                          className="watchlist-eye__list-item-owner"
                          noWrap
                        >
                          {cert.compliance_user?.first_name &&
                          cert.compliance_user?.last_name
                            ? `${cert.compliance_user.first_name} ${cert.compliance_user.last_name}`
                            : cert.compliance_user?.first_name ||
                              'No file owner'}
                        </Typography>
                      </Box>
                    </Box>
                  }
                  secondaryTypographyProps={{ component: 'div' }}
                />
                <ListItemSecondaryAction className="watchlist-eye__list-item-secondary-action">
                  <Tooltip title="Stop watching">
                    <MuiIconButton
                      edge="end"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUnwatch(cert.id);
                      }}
                      className="watchlist-eye__unwatch-icon"
                    >
                      <VisibilityOffIcon fontSize="small" />
                    </MuiIconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </MenuPopover>
    </>
  );
}
